// src/pages/AboutShuihe.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
} from "@mui/material";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";
import Brands from "../../LandingPages/b2b/Brands";

/* Assets */
const TILE_COMPANY_IMG = "/img/aboutus/company.webp";
const TILE_ESG_IMG = "/img/aboutus/sustain.webp";

/* -------- helpers: language from cookie + localized text -------- */
const TEXTS = {
  ZH: {
    heroTitle: "關於水禾",
    heroSubtitle: "在意任何小細節，為您的健康把關",
    sectionTitle: "關於水禾",
    body: [
      "台灣水禾的核心理念，「潔淨初心  守護健康」",
      "致力於為社會大眾、產業夥伴提供專業可靠的衛生防護與清潔病媒防治方案。",
      "我們相信，健康不僅是個人資產，更是社會與環境的共同責任，",
      "因此積極推動兼具創新、專業與永續的產品與服務。",
    ],
    tiles: {
      company: "公司簡介",
      responsibility: "企業責任",
    },
    brandTitle: "品牌資訊",
  },
  EN: {
    heroTitle: "About Seho",
    heroSubtitle: "Caring for every detail to safeguard your health.",
    sectionTitle: "About Seho",
    body: [
      "At SEHO Taiwan, “Cleanliness and Health Protection” guides our mission to provide trusted hygiene and vector-control solutions.",
      "We uphold health as a shared responsibility of individuals, society, and the environment, and continue to advance innovative, professional, and sustainable offerings.",
    ],
    tiles: {
      company: "Company Profile",
      responsibility: "Corporate Responsibility",
    },
    brandTitle: "Brand Information",
  },
  JP: {
    heroTitle: "水禾について",
    heroSubtitle: "小さなことにも気を配り、お客様の健康をしっかり守ります。",
    sectionTitle: "水禾について",
    body: [
      "台灣水禾 (Taiwan SEHO) は、「ピュアな心、健康を守る」というコアバリューを掲げています。",
      "生活者の皆さまおよび産業パートナーの皆さまに対し、信頼性の高い衛生対策と清掃・防疫ソリューションを提供することに努めております。",
      "私たちは、健康は個人の資産であると共に、社会と環境が共有すべき責任でもあると考えております。",
      "そのため、革新性・専門性・持続可能性を兼ね備えた製品・サービスをお届けしてまいります。",
    ],
    tiles: {
      company: "会社概要",
      responsibility: "企業の社会的責任",
    },
    brandTitle: "ブランド情報",
  },
};

function getCurrentLang() {
  if (typeof window === "undefined") return "ZH";
  const fromCookie = getLangFromCookie();
  const fromLS = window.localStorage.getItem(LANG_COOKIE);
  const raw = fromCookie || fromLS || "ZH";
  // normalize
  if (raw === "EN" || raw === "JP" || raw === "ZH") return raw;
  return "ZH";
}

export default function AboutShuihe({
  fonts,
  titleColor,
  bg = "background.default",
}) {
  const heroImage = "/img/aboutus/about-us-office.webp";

  const [lang, setLang] = useState(getCurrentLang);

  useEffect(() => {
    const handler = () => {
      setLang(getCurrentLang());
    };
    window.addEventListener("app:languageChanged", handler);
    return () => window.removeEventListener("app:languageChanged", handler);
  }, []);

  const t = TEXTS[lang] || TEXTS.ZH;

  return (
    <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: bg }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: "100%",
          maxWidth: 1200,
          px: { xs: 2, sm: 3, md: 4 },
          mx: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "minmax(0, 0.95fr) minmax(0, 1.25fr)",
            },
            gap: { xs: 3, sm: 3.5, md: 4 },
            alignItems: "center",
            bgcolor: "background.paper",
            px: { xs: 0, sm: 1, md: 0 },
            py: { xs: 3, sm: 4, md: 6 },
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {/* LEFT SIDE — IMAGE */}
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              minHeight: { xs: 220, sm: 260, md: 300 },
              aspectRatio: { xs: "16 / 10", md: "16 / 9" },
              backgroundImage: `url(${heroImage})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: { xs: 1, md: 1 },
            }}
          />

          {/* RIGHT SIDE — TEXT */}
          <Box
            sx={{
              minWidth: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "left",
              overflowWrap: "anywhere",
              wordBreak: "normal",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontFamily: fonts[lang],
                letterSpacing: 1,
                color: titleColor,
                fontSize: { xs: 20, sm: 22, md: 24 },
                mb: 2,
              }}
            >
              {t.sectionTitle}
            </Typography>

            <Typography
              sx={{
                lineHeight: 2,
                fontSize: { xs: 15, sm: 16, md: 20 },
                color: "text.primary",
              }}
            >
              {t.body.map((line, idx) => (
                <React.Fragment key={idx}>
                  {line}
                  {idx !== t.body.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Brand list – Brands handles its own mode/lang; we only pass colors */}
      <Brands titleColor="text.primary" bgcolor="#fffffc" border={false}/>
    </Box>
  );
}
